'use client'

import { useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { cn } from '@/lib/utils'
import { AppStoreBadge, PlayStoreBadge } from '@/shared/not-found/components/store-badges'
import { HUBHIVE_APP, type StoreApp } from '@/shared/store-apps.constants'
import { detectPlatform, type WebPlatform } from './platform'

/**
 * Inline "get the app" CTA for marketing surfaces (landing GetAppCTA, CTA
 * footer, the `/loop` download page). Same convention as `AppInviteModal`: the
 * CTA is keyed on OS, not viewport — desktop gets a QR to scan with a phone;
 * iOS/Android web get tappable store badges, leading with the device's own
 * store.
 */
export function AppDownloadCTA({
  qrSize = 140,
  className,
  badgesAboveQr = false,
  app = HUBHIVE_APP,
  initialPlatform = 'desktop',
  otherStoreAsLink = false,
}: {
  qrSize?: number
  className?: string
  /** Which app to offer. Defaults to the main HubHive app. */
  app?: StoreApp
  /**
   * Desktop only: render tappable App Store + Google Play badges directly above
   * the QR code. On mobile the store badges already lead, so this is a no-op
   * there (avoids showing the badges twice).
   */
  badgesAboveQr?: boolean
  /**
   * The platform a server component already read off the request's user-agent
   * (`platformFromUserAgent`), so the FIRST paint is right for a phone. Without
   * it the component renders the desktop QR during SSR and swaps once
   * `detectPlatform()` runs after hydration — on the phone that just scanned a
   * QR, that is a flash of another QR. The client read still runs after mount
   * and may refine the seed: iPadOS reports a desktop UA and is only visible to
   * a DOM touch check.
   */
  initialPlatform?: WebPlatform
  /**
   * Phone only: render just the device's own store badge, and offer the other
   * store as a small text link beneath it instead of a second badge. For a
   * surface whose one job is "install this" — a QR landing — two equal badges
   * ask the visitor to choose; one badge tells them. The other store stays one
   * tap away for the odd misdetected browser.
   */
  otherStoreAsLink?: boolean
}) {
  // OS is client-only in general; resolve after mount. The seed defaults to
  // 'desktop' → QR, the safe fallback (mirrors AppInviteModal), unless the
  // server already knew better (see `initialPlatform`).
  const [platform, setPlatform] = useState<WebPlatform>(initialPlatform)
  useEffect(() => {
    setPlatform(detectPlatform())
  }, [])

  if (platform === 'desktop') {
    const qr = (
      <a
        href={app.downloadUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Get the ${app.name} app`}
        className="inline-block rounded-xl bg-white p-3 ring-1 ring-border transition-opacity hover:opacity-90"
      >
        <QRCodeSVG value={app.downloadUrl} size={qrSize} />
      </a>
    )

    if (badgesAboveQr) {
      return (
        <div className={cn('flex flex-col gap-4', className)}>
          <div className="flex flex-wrap items-center gap-3">
            <AppStoreBadge app={app} />
            <PlayStoreBadge app={app} />
          </div>
          {qr}
        </div>
      )
    }

    return <div className={cn('flex justify-center', className)}>{qr}</div>
  }

  if (otherStoreAsLink) {
    const other =
      platform === 'android'
        ? { href: app.appStoreUrl, text: 'Also on the App Store' }
        : { href: app.playStoreUrl, text: 'Also on Google Play' }

    return (
      <div className={cn('flex flex-col items-center gap-4', className)}>
        {platform === 'android' ? <PlayStoreBadge app={app} /> : <AppStoreBadge app={app} />}
        <a
          href={other.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
        >
          {other.text}
        </a>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-wrap items-center justify-center gap-3', className)}>
      {platform === 'android' ? (
        <>
          <PlayStoreBadge app={app} />
          <AppStoreBadge app={app} />
        </>
      ) : (
        <>
          <AppStoreBadge app={app} />
          <PlayStoreBadge app={app} />
        </>
      )}
    </div>
  )
}
